// pages/CreateArticlePage.js
// ─────────────────────────────────────────────────────────────────────────────
// Doctor-only page to create an article / video / white paper.
//
// Backend API expected:
//  POST /api/articles
//  Headers:
//    Authorization: Bearer <token>
//    X-Tenant-ID: <tenantId>
//    Content-Type: multipart/form-data   (when files are attached)
//              OR application/json       (text-only)
//
//  Fields:
//    type*         : "ARTICLE" | "VIDEO" | "WHITEPAPER"
//    title*        : string (max 200)
//    summary*      : string (max 500)
//    body          : string
//    tags          : comma-separated string
//    thumbnailFile : File (image)
//    videoFile     : File (video, mutually exclusive with videoUrl)
//    videoUrl      : string (YouTube/Vimeo URL, mutually exclusive with videoFile)
//    externalUrl   : string
//
//  Success 201: { id, doctorId, type, title, summary, body, tags[],
//                 thumbnailUrl, videoUrl, publishedDate, viewCount, externalUrl }
//  Error 400/422: { message, errors: { field: "message" } }
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { API_BASE_URL as BASE_URL } from '../services/apiBaseUrl';

const TYPE_OPTIONS = [
  { value: 'ARTICLE',    label: 'Article' },
  { value: 'VIDEO',      label: 'Video' },
  { value: 'WHITEPAPER', label: 'White Paper' },
];

const SUGGESTED_TAGS = [
  'Cardiology', 'Diabetes', 'Pediatrics', 'Orthopaedics', 'Neurology',
  'Nutrition', 'Mental Health', 'Dermatology', 'Oncology', 'Endocrinology',
  'Heart Health', 'Child Health', 'Exercise', 'Vaccines', 'Diagnostics',
  'Hypertension', 'Back Pain', 'Fasting', 'Antibiotics', "Women's Health",
];

const MAX_IMAGE_MB = 5;
const MAX_VIDEO_MB = 200;
const ACCEPT_IMAGE = 'image/jpeg,image/png,image/webp,image/gif';
const ACCEPT_VIDEO = 'video/mp4,video/webm,video/ogg,video/quicktime';

const isValidVideoUrl = (url) =>
  url && /^https?:\/\/.+/.test(url);

const formatBytes = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ── Shared styles ───────────────────────────────────────────── */
const inputStyle = {
  width: '100%', boxSizing: 'border-box',
  height: 44, padding: '0 14px',
  border: '1.5px solid #e5e7eb', borderRadius: 10,
  fontSize: 14, fontFamily: 'DM Sans, sans-serif', color: '#1e1b4b',
  background: '#fff', outline: 'none', transition: 'border-color .15s',
};

const textareaStyle = {
  ...inputStyle, height: 'auto', padding: '12px 14px',
  resize: 'vertical', lineHeight: 1.65,
};

/* ── Small components ────────────────────────────────────────── */
const FieldError = ({ msg }) =>
  msg ? (
    <p style={{ margin: '4px 0 0', fontSize: 12, color: '#dc2626', fontFamily: 'DM Sans, sans-serif' }}>
      {msg}
    </p>
  ) : null;

const Label = ({ children, required }) => (
  <label style={{
    display: 'block', marginBottom: 6,
    fontSize: 13, fontWeight: 700, color: '#374151',
    fontFamily: 'DM Sans, sans-serif',
  }}>
    {children}
    {required && <span style={{ color: '#dc2626', marginLeft: 3 }}>*</span>}
  </label>
);

const Section = ({ title, children }) => (
  <div className="card" style={{ marginBottom: 16 }}>
    <div style={{
      marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f3f4f6',
    }}>
      <h2 style={{
        margin: 0, fontFamily: 'Sora, sans-serif',
        fontWeight: 700, fontSize: 14.5, color: '#1e1b4b',
      }}>{title}</h2>
    </div>
    {children}
  </div>
);

/* ── Image drop zone ─────────────────────────────────────────── */
const ImageDropZone = ({ file, preview, onFile, onClear, error }) => {
  const inputRef = useRef();
  const [drag, setDrag] = useState(false);

  const handleDrop = useCallback(e => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files[0];
    if (f) onFile(f);
  }, [onFile]);

  return (
    <div>
      {preview ? (
        <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', border: '1.5px solid #e5e7eb' }}>
          <img
            src={preview}
            alt="Thumbnail preview"
            style={{ width: '100%', height: 180, objectFit: 'cover', display: 'block' }}
          />
          <button
            type="button"
            onClick={onClear}
            style={{
              position: 'absolute', top: 8, right: 8,
              width: 28, height: 28, borderRadius: '50%',
              background: 'rgba(0,0,0,0.55)', color: '#fff',
              border: 'none', cursor: 'pointer', fontSize: 15,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
          <div style={{
            position: 'absolute', bottom: 8, left: 10,
            fontSize: 11, fontWeight: 600, color: '#fff',
            background: 'rgba(0,0,0,0.5)', padding: '2px 7px',
            borderRadius: 5, fontFamily: 'DM Sans, sans-serif',
          }}>
            {file.name} · {formatBytes(file.size)}
          </div>
        </div>
      ) : (
        <div
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          style={{
            border: `2px dashed ${drag ? '#4f46e5' : error ? '#dc2626' : '#d1d5db'}`,
            borderRadius: 10, padding: '24px 16px',
            textAlign: 'center', cursor: 'pointer',
            background: drag ? '#eef2ff' : '#fafafa',
            transition: 'all .15s',
          }}
        >
          <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13.5, color: '#374151', marginBottom: 4 }}>
            Drop an image or click to browse
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>
            JPEG, PNG, WebP, GIF · Max {MAX_IMAGE_MB} MB
          </div>
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT_IMAGE}
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }}
      />
      <FieldError msg={error} />
    </div>
  );
};

/* ── Video source picker ─────────────────────────────────────── */
const VideoSection = ({
  videoSource, setVideoSource,
  videoUrl, setVideoUrl, videoUrlError,
  videoFile, onVideoFile, onVideoClear, videoFileError,
}) => (
  <div>
    <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
      {[
        { v: 'none',   label: 'None' },
        { v: 'url',    label: 'YouTube / Link' },
        { v: 'upload', label: 'Upload file' },
      ].map(opt => (
        <button
          key={opt.v}
          type="button"
          onClick={() => setVideoSource(opt.v)}
          style={{
            flex: 1, padding: '8px 6px', borderRadius: 9, cursor: 'pointer',
            border: `1.5px solid ${videoSource === opt.v ? '#4f46e5' : '#e5e7eb'}`,
            background: videoSource === opt.v ? '#eef2ff' : '#fff',
            color: videoSource === opt.v ? '#4f46e5' : '#6b7280',
            fontSize: 12.5, fontWeight: videoSource === opt.v ? 700 : 500,
            fontFamily: 'DM Sans, sans-serif', transition: 'all .15s',
          }}
        >
          {opt.label}
        </button>
      ))}
    </div>

    {videoSource === 'url' && (
      <div>
        <input
          type="url"
          placeholder="https://www.youtube.com/watch?v=..."
          value={videoUrl}
          onChange={e => setVideoUrl(e.target.value)}
          style={{
            ...inputStyle,
            borderColor: videoUrlError ? '#dc2626' : videoUrl && isValidVideoUrl(videoUrl) ? '#059669' : '#e5e7eb',
          }}
        />
        {videoUrl && isValidVideoUrl(videoUrl) && !videoUrlError && (
          <p style={{ margin: '4px 0 0', fontSize: 12, color: '#059669', fontFamily: 'DM Sans, sans-serif' }}>
            Valid URL
          </p>
        )}
        <FieldError msg={videoUrlError} />
      </div>
    )}

    {videoSource === 'upload' && (
      <div>
        {videoFile ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
            border: '1.5px solid #e5e7eb', borderRadius: 10, background: '#f9fafb',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#1e1b4b', fontFamily: 'DM Sans, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {videoFile.name}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
                {formatBytes(videoFile.size)}
              </div>
            </div>
            <button
              type="button"
              onClick={onVideoClear}
              style={{
                width: 26, height: 26, borderRadius: '50%', background: '#f3f4f6',
                border: 'none', cursor: 'pointer', fontSize: 14, color: '#6b7280',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}
            >×</button>
          </div>
        ) : (
          <label style={{
            display: 'block', border: '2px dashed #d1d5db',
            borderRadius: 10, padding: '22px 16px',
            textAlign: 'center', cursor: 'pointer', background: '#fafafa',
          }}>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 13.5, color: '#374151', marginBottom: 4 }}>
              Click to upload a video file
            </div>
            <div style={{ fontSize: 12, color: '#9ca3af', fontFamily: 'DM Sans, sans-serif' }}>
              MP4, WebM, MOV · Max {MAX_VIDEO_MB} MB
            </div>
            <input
              type="file"
              accept={ACCEPT_VIDEO}
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files[0]) onVideoFile(e.target.files[0]); }}
            />
          </label>
        )}
        <FieldError msg={videoFileError} />
      </div>
    )}
  </div>
);

/* ── Tag input ───────────────────────────────────────────────── */
const TagInput = ({ tags, setTags, error }) => {
  const [input, setInput] = useState('');

  const addTag = (tag) => {
    const t = tag.trim();
    if (!t || tags.includes(t) || tags.length >= 10) return;
    setTags([...tags, t]);
    setInput('');
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addTag(input); }
    if (e.key === 'Backspace' && !input && tags.length > 0) setTags(tags.slice(0, -1));
  };

  return (
    <div>
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          {tags.map(tag => (
            <span key={tag} style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '4px 10px', borderRadius: 9999,
              background: '#eef2ff', color: '#4f46e5', border: '1px solid #c7d2fe',
              fontSize: 12, fontWeight: 600, fontFamily: 'DM Sans, sans-serif',
            }}>
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#6366f1', fontSize: 13, padding: 0, lineHeight: 1,
                  display: 'flex', alignItems: 'center',
                }}
              >×</button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        placeholder={tags.length >= 10 ? 'Max 10 tags reached' : 'Type a tag, press Enter or comma…'}
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={handleKey}
        onBlur={() => input.trim() && addTag(input)}
        disabled={tags.length >= 10}
        style={{ ...inputStyle, borderColor: error ? '#dc2626' : '#e5e7eb' }}
      />

      {tags.length < 10 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 8 }}>
          {SUGGESTED_TAGS.filter(t => !tags.includes(t)).slice(0, 12).map(tag => (
            <button
              key={tag}
              type="button"
              onClick={() => addTag(tag)}
              style={{
                padding: '3px 9px', borderRadius: 9999,
                border: '1px solid #e5e7eb', background: '#f9fafb',
                fontSize: 11.5, color: '#6b7280', cursor: 'pointer',
                fontFamily: 'DM Sans, sans-serif', transition: 'all .12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.color = '#4f46e5'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
            >
              + {tag}
            </button>
          ))}
        </div>
      )}
      <FieldError msg={error} />
    </div>
  );
};

/* ── Main Page ───────────────────────────────────────────────── */
const CreateArticlePage = () => {
  const navigate        = useNavigate();
  const { token }       = useAuth();
  const { tenant }      = useTenant();

  const [type,         setType]        = useState('ARTICLE');
  const [title,        setTitle]       = useState('');
  const [summary,      setSummary]     = useState('');
  const [body,         setBody]        = useState('');
  const [tags,         setTags]        = useState([]);
  const [externalUrl,  setExternalUrl] = useState('');

  const [thumbFile,    setThumbFile]    = useState(null);
  const [thumbPreview, setThumbPreview] = useState('');
  const [videoSource,  setVideoSource]  = useState('none');
  const [videoUrl,     setVideoUrl]     = useState('');
  const [videoFile,    setVideoFile]    = useState(null);

  const [errors,       setErrors]      = useState({});
  const [submitting,   setSubmitting]  = useState(false);
  const [success,      setSuccess]     = useState(false);
  const [serverError,  setServerError] = useState('');

  /* ── Handlers ─────────────────────────────── */
  const handleThumbFile = (file) => {
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      setErrors(e => ({ ...e, thumbnail: `Image must be under ${MAX_IMAGE_MB} MB` }));
      return;
    }
    setErrors(e => ({ ...e, thumbnail: '' }));
    setThumbFile(file);
    const reader = new FileReader();
    reader.onload = () => setThumbPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleClearThumb    = () => { setThumbFile(null); setThumbPreview(''); };
  const handleVideoFile     = (file) => {
    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      setErrors(e => ({ ...e, videoFile: `Video must be under ${MAX_VIDEO_MB} MB` }));
      return;
    }
    setErrors(e => ({ ...e, videoFile: '' }));
    setVideoFile(file);
  };
  const handleClearVideoFile = () => setVideoFile(null);
  const handleVideoSourceChange = (v) => {
    setVideoSource(v);
    setVideoUrl('');
    setVideoFile(null);
    setErrors(e => ({ ...e, videoUrl: '', videoFile: '' }));
  };
  const handleTypeChange = (t) => {
    setType(t);
    if (t !== 'VIDEO') { setVideoSource('none'); setVideoUrl(''); setVideoFile(null); }
  };

  /* ── Validation ───────────────────────────── */
  const validate = () => {
    const e = {};
    if (!title.trim())                    e.title   = 'Title is required';
    else if (title.trim().length > 200)   e.title   = 'Max 200 characters';
    if (!summary.trim())                  e.summary = 'Summary is required';
    else if (summary.trim().length > 500) e.summary = 'Max 500 characters';
    if (type === 'VIDEO' && videoSource === 'url' && videoUrl && !isValidVideoUrl(videoUrl))
      e.videoUrl = 'Enter a valid URL starting with http';
    return e;
  };

  /* ── Submit ───────────────────────────────── */
  const handleSubmit = async () => {
    setServerError('');
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    setSubmitting(true);
    try {
      const hasFile = !!thumbFile || !!videoFile;
      let res;

      if (hasFile) {
        const fd = new FormData();
        fd.append('type',    type);
        fd.append('title',   title.trim());
        fd.append('summary', summary.trim());
        if (body.trim())        fd.append('body',        body.trim());
        if (tags.length > 0)    fd.append('tags',        tags.join(','));
        if (externalUrl.trim()) fd.append('externalUrl', externalUrl.trim());
        if (thumbFile)          fd.append('thumbnailFile', thumbFile);
        if (videoFile)          fd.append('videoFile',   videoFile);
        if (videoSource === 'url' && videoUrl.trim())
                                fd.append('videoUrl',    videoUrl.trim());

        res = await fetch(`${BASE_URL}/articles`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
          },
          body: fd,
        });
      } else {
        res = await fetch(`${BASE_URL}/articles`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:  `Bearer ${token}`,
            ...(tenant?.id ? { 'X-Tenant-ID': tenant.id } : {}),
          },
          body: JSON.stringify({
            type,
            title:      title.trim(),
            summary:    summary.trim(),
            body:       body.trim() || undefined,
            tags,
            externalUrl: externalUrl.trim() || undefined,
            videoUrl:   videoSource === 'url' && videoUrl.trim() ? videoUrl.trim() : undefined,
          }),
        });
      }

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.errors) setErrors(data.errors);
        setServerError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      const created = await res.json().catch(() => ({}));
      setSuccess(true);
      setTimeout(() => {
        if (created?.id) navigate(`/articles/${created.id}`);
        else navigate('/doctor/dashboard');
      }, 1600);

    } catch {
      setServerError('Could not connect to the server. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Success screen ───────────────────────── */
  if (success) return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: 520, textAlign: 'center', paddingTop: 80 }}>
        <div style={{ fontSize: 48, marginBottom: 14 }}>🎉</div>
        <h1 className="t-h1" style={{ marginBottom: 8 }}>Published!</h1>
        <p className="t-body-sm">Redirecting you to the article…</p>
        <style>{`@keyframes progressFill { from { width: 0 } to { width: 100% } }`}</style>
        <div style={{ width: 48, height: 3, borderRadius: 2, background: '#eef2ff', margin: '20px auto 0', overflow: 'hidden' }}>
          <div style={{ height: '100%', background: '#4f46e5', borderRadius: 2, animation: 'progressFill 1.6s linear forwards' }} />
        </div>
      </main>
    </div>
  );

  const charTitle   = title.length;
  const charSummary = summary.length;

  return (
    <div className="page">
      <main className="page-main" style={{ maxWidth: 700 }}>

        {/* Breadcrumb */}
        <div className="breadcrumb" style={{ marginBottom: 18 }}>
          <button className="t-link" onClick={() => navigate('/doctor/dashboard')}>Dashboard</button>
          <span>›</span>
          <span className="bc-current">Create Content</span>
        </div>

        {/* Header */}
        <div className="fade-up" style={{ marginBottom: 20 }}>
          <h1 className="t-h1" style={{ marginBottom: 4 }}>Create Content</h1>
          <p className="t-body-sm">
            Publish an article, video, or white paper — visible on your profile and the Health Insights feed.
          </p>
        </div>

        {/* Type pill tabs */}
        <div className="fade-up-1" style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
          {TYPE_OPTIONS.map(opt => {
            const active = type === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handleTypeChange(opt.value)}
                className={`pill-filter${active ? ' pill-filter--active' : ''}`}
                style={{ fontSize: 13 }}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Basic Details */}
        <div className="fade-up-1">
          <Section title="Basic Details">

            <div style={{ marginBottom: 16 }}>
              <Label required>Title</Label>
              <input
                type="text"
                placeholder={
                  type === 'VIDEO'      ? 'e.g. How to Read Your ECG Report' :
                  type === 'WHITEPAPER' ? 'e.g. Statin Therapy in South Asian Populations' :
                                          'e.g. Understanding Hypertension: The Silent Killer'
                }
                value={title}
                maxLength={200}
                onChange={e => { setTitle(e.target.value); setErrors(err => ({ ...err, title: '' })); }}
                style={{ ...inputStyle, borderColor: errors.title ? '#dc2626' : '#e5e7eb' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <FieldError msg={errors.title} />
                <span style={{ fontSize: 11.5, color: charTitle > 180 ? '#d97706' : '#9ca3af', fontFamily: 'DM Sans, sans-serif', marginLeft: 'auto' }}>
                  {charTitle}/200
                </span>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <Label required>Summary</Label>
              <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
                Shown on cards and search results. Keep it concise.
              </p>
              <textarea
                placeholder="A short description of what this covers…"
                value={summary}
                maxLength={500}
                rows={3}
                onChange={e => { setSummary(e.target.value); setErrors(err => ({ ...err, summary: '' })); }}
                style={{ ...textareaStyle, borderColor: errors.summary ? '#dc2626' : '#e5e7eb' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                <FieldError msg={errors.summary} />
                <span style={{ fontSize: 11.5, color: charSummary > 460 ? '#d97706' : '#9ca3af', fontFamily: 'DM Sans, sans-serif', marginLeft: 'auto' }}>
                  {charSummary}/500
                </span>
              </div>
            </div>

            <div>
              <Label>Full Body <span style={{ fontWeight: 400, color: '#9ca3af', fontSize: 12 }}>(optional)</span></Label>
              <p style={{ margin: '0 0 6px', fontSize: 12, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
                Write the complete content. Markdown is supported.
              </p>
              <textarea
                placeholder="Write the full content here…"
                value={body}
                rows={10}
                onChange={e => setBody(e.target.value)}
                style={{ ...textareaStyle, minHeight: 200 }}
              />
            </div>
          </Section>
        </div>

        {/* Thumbnail */}
        <div className="fade-up-2">
          <Section title="Thumbnail Image (optional)">
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
              Displayed as the card cover. If omitted, a placeholder is shown.
            </p>
            <ImageDropZone
              file={thumbFile}
              preview={thumbPreview}
              onFile={handleThumbFile}
              onClear={handleClearThumb}
              error={errors.thumbnail}
            />
          </Section>
        </div>

        {/* Video — only for VIDEO type */}
        {type === 'VIDEO' && (
          <div className="fade-up-2">
            <Section title="Video Source (optional)">
              <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
                Link a YouTube / Vimeo URL or upload a video file. Only one source at a time.
              </p>
              <VideoSection
                videoSource={videoSource}
                setVideoSource={handleVideoSourceChange}
                videoUrl={videoUrl}
                setVideoUrl={v => { setVideoUrl(v); setErrors(e => ({ ...e, videoUrl: '' })); }}
                videoUrlError={errors.videoUrl}
                videoFile={videoFile}
                onVideoFile={handleVideoFile}
                onVideoClear={handleClearVideoFile}
                videoFileError={errors.videoFile}
              />
            </Section>
          </div>
        )}

        {/* External link — article / whitepaper */}
        {type !== 'VIDEO' && (
          <div className="fade-up-2">
            <Section title="External Link (optional)">
              <p style={{ margin: '0 0 10px', fontSize: 12.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
                Link to the full paper on an external site, e.g. PubMed or a journal page.
              </p>
              <input
                type="url"
                placeholder="https://pubmed.ncbi.nlm.nih.gov/..."
                value={externalUrl}
                onChange={e => setExternalUrl(e.target.value)}
                style={inputStyle}
              />
            </Section>
          </div>
        )}

        {/* Tags */}
        <div className="fade-up-2">
          <Section title="Tags">
            <p style={{ margin: '0 0 12px', fontSize: 12.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }}>
              Add up to 10 tags. Press Enter or comma to confirm.
            </p>
            <TagInput tags={tags} setTags={setTags} error={errors.tags} />
          </Section>
        </div>

        {/* Server error */}
        {serverError && (
          <div className="error-state" style={{ marginBottom: 14 }}>
            <span>⚠️</span> {serverError}
          </div>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex', gap: 10, justifyContent: 'flex-end',
          flexWrap: 'wrap', marginBottom: 48,
        }}>
          <button
            type="button"
            onClick={() => navigate('/doctor/dashboard')}
            disabled={submitting}
            style={{
              padding: '11px 22px', borderRadius: 10,
              border: '1.5px solid #e5e7eb', background: '#fff',
              color: '#6b7280', fontSize: 13.5, fontWeight: 600,
              fontFamily: 'DM Sans, sans-serif', cursor: submitting ? 'not-allowed' : 'pointer',
              transition: 'all .12s',
            }}
            onMouseEnter={e => { if (!submitting) { e.currentTarget.style.borderColor = '#4f46e5'; e.currentTarget.style.color = '#4f46e5'; } }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#e5e7eb'; e.currentTarget.style.color = '#6b7280'; }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="btn-primary"
            style={{ minWidth: 160, padding: '11px 24px', fontSize: 13.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            {submitting ? (
              <>
                <span style={{ width: 15, height: 15, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin .6s linear infinite' }} />
                Publishing…
              </>
            ) : (
              `Publish ${TYPE_OPTIONS.find(t => t.value === type)?.label}`
            )}
          </button>
        </div>

        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      </main>
    </div>
  );
};

export default CreateArticlePage;